import { useState } from "react";
import {
    FunctionField,
    ListContextProvider,
    NumberField,
    SimpleShowLayout,
    TextField,
    useList,
    usePermissions,
    useRecordContext,
    useResourceContext,
    useGetOne,
    useNotify,
} from "react-admin";
import { Box, CircularProgress } from "@mui/material";
import { queryToAttribute } from "shared/src/utils/decodeQuery";
import { ToolbarWithHistory } from "shared/src/components/ToolbarWithHistory";
import { AccessRightsError } from "shared/src/components/AccessRightsError";
import { RaDateField } from "shared/src/components/fields/Ra/RaDateField";
import { RaShowView } from "shared/src/components/views/RaShowView";
import { DatagridWithHistory } from "shared/src/components/DatagridWithHistory";
import { handleHttpError } from "shared/src/utils/errorHandler";
import { useInfiniteScroll } from "shared/src/hooks/useInfiniteScroll";
import { PrintLabels } from "../components/PrintLabels";

export const ArrivalShow = (props) => {
    const [printLabels, setPrintLabels] = useState(false);
    const resource = useResourceContext();
    const attribute = queryToAttribute();
    const { isLoading, permissions } = usePermissions();

    if (isLoading) {
        return <></>;
    } else if (!permissions.vote({ resource, attribute })) {
        return <AccessRightsError />;
    }
    return (
        <RaShowView
            {...props}
            actions={
                <ToolbarWithHistory
                    back={true}
                    edit={true}
                    print={true}
                    setPrintLabels={setPrintLabels}
                />
            }
        >
            {printLabels && <PrintLabels setPrintLabels={setPrintLabels} />}
            <SimpleShowLayout>
                <RaDateField label="equip.orderDate" source="orderDate" />
                <NumberField
                    label="equip.purchasePrice"
                    source="purchasePrice"
                    options={{ style: "currency", currency: "EUR" }}
                />
                <RaDateField label="equip.deliveryDate" source="deliveryDate" />
                <TextField label="equip.user" source="createdBy.email" />
                <TextField label="equip.purchaseOrderNumber" source="purchaseOrderNumber" />
                <NumberField label="equip.quantity" source="quantity" />
                <TextField label="equip.comment" source="comment" />
            </SimpleShowLayout>
            <SkuTable />
        </RaShowView>
    );
};

const SkuTable = () => {
    const notify = useNotify();
    const record = useRecordContext();

    const { data: productData } = useGetOne(
        "products",
        { id: record?.product?.id },
        {
            onError: (error) => handleHttpError(error, notify),
            enabled: !!record?.product?.id,
        }
    );

    const {
        data: skus,
        isPending,
        isFetchingNextPage,
        observerRef,
    } = useInfiniteScroll(
        "skus",
        {
            filter: { arrival: record?.id },
            pagination: { page: 1, perPage: 25 },
            sort: { field: "id", order: "ASC" },
        },
        { enabled: !!record?.id && !productData?.isMassProduct }
    );

    const listContext = useList({ data: skus, resource: "skus" });

    if (!record?.id || productData?.isMassProduct) {
        return <></>;
    }

    if (isPending) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3, mt: 5 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <ListContextProvider value={listContext}>
                <DatagridWithHistory
                    sx={{ marginTop: 5 }}
                    bulkActionButtons={false}
                    rowClick={(id, resource, record) => {
                        return `/skus/${record.id}/show`;
                    }}
                >
                    <TextField label="equip.labelNumber" source="labelNumber" />
                    <TextField label="equip.manufacturer" source="manufacturer.name" />
                    <TextField label="equip.supplier" source="supplier.name" />
                    <NumberField
                        label="equip.currentPrice"
                        source="currentPrice"
                        options={{ style: "currency", currency: "EUR" }}
                        sortable={false}
                    />
                    <NumberField
                        label="equip.weight"
                        source="weight"
                        options={{
                            style: "unit",
                            unit: "kilogram",
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                        }}
                    />
                    <FunctionField
                        label="equip.status"
                        render={(sku) => sku.status}
                        source="status"
                    />
                    <TextField label="equip.serialNumber" source="serialNumber" />
                </DatagridWithHistory>
            </ListContextProvider>
            <Box ref={observerRef} sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                {isFetchingNextPage && <CircularProgress size={24} />}
            </Box>
        </>
    );
};
